// components/PdfRenderer/PdfRenderer.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import './p.css';
import { PdfService } from '../services/pdfService';

/**
 * PdfRenderer
 *
 * Changes applied for visual comfort:
 * - Added `filter` style to <Page> to reduce brightness (0.94) and add slight warmth (sepia 0.03).
 * - Set transparent backgrounds on wrappers to prevent white flashing.
 */
export default function PdfRenderer({
  filePath,
  pageNumber,
  zoom,
  deviceScale,
  onDocumentLoadSuccess,
  onPdfNewlyRendered,
  children
}) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasNotified, setHasNotified] = useState(false);
  const [currentFilePath, setCurrentFilePath] = useState(null);

  const documentLoadedRef = useRef(false);

  // 1. Reset state when filePath changes
  useEffect(() => {
    if (!filePath) {
      setPdfUrl(null);
      setLoading(false);
      setError(null);
      setHasNotified(false);
      documentLoadedRef.current = false;
      setCurrentFilePath(null);
      return;
    }

    if (filePath !== currentFilePath) {
      console.log('🔄 PdfRenderer: document changed -> resetting local state');
      setPdfUrl(null);
      setLoading(false);
      setError(null);
      setHasNotified(false);
      documentLoadedRef.current = false;
      setCurrentFilePath(filePath);
    }
  }, [filePath]);

  // 2. Load PDF when currentFilePath is set
  useEffect(() => {
    if (!currentFilePath) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        console.log('📥 PdfRenderer: Fetching PDF URL for', currentFilePath);
        const url = await PdfService.getPdf(currentFilePath);

        if (!isMounted) return;
        if (!url) throw new Error('PdfService returned an empty url');

        setPdfUrl(url);
      } catch (err) {
        if (!isMounted) return;
        console.error('❌ PdfRenderer: Error loading PDF', err);
        setError(err?.message || String(err));
        setPdfUrl(null);
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [currentFilePath]);

  // 3. Handlers
  const handleDocumentLoadSuccess = (pdf) => {
    if (!documentLoadedRef.current && typeof onPdfNewlyRendered === 'function') {
      try {
        onPdfNewlyRendered();
      } catch (e) {
        console.error(e);
      }
      documentLoadedRef.current = true;
      setHasNotified(true);
    }
    if (typeof onDocumentLoadSuccess === 'function') {
      onDocumentLoadSuccess(pdf);
    }
  };

  const handleDocumentLoadError = (err) => {
    console.error('❌ PdfRenderer: document load error', err);
    documentLoadedRef.current = false;
    setHasNotified(false);
    setError(err?.message || String(err));
  };

  const retryLoad = () => {
    setError(null);
    setCurrentFilePath(null);
    setTimeout(() => setCurrentFilePath(filePath), 100);
  };

  // --- RENDER ---

  if (loading) {
    return (
      <div className="pdf-loading">
        <div className="loading-spinner" />
        <div>Loading PDF...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-error">
        <div>Error: {error}</div>
        <button onClick={retryLoad} style={{ marginTop: '10px' }}>Retry</button>
      </div>
    );
  }

  if (!pdfUrl || !currentFilePath) {
    return <div className="pdf-placeholder">Select a document</div>;
  }

  return (
    <Document
      file={pdfUrl}
      onLoadSuccess={handleDocumentLoadSuccess}
      onLoadError={handleDocumentLoadError}
      loading={<div className="pdf-loading"><div className="loading-spinner" /></div>}
      error={<div className="pdf-error">Failed to load</div>}
      className="pdf-document-container"
    >
      <div id="pdf-viewer-wrapper">
        <div id="pdf-viewer-container" className="pdf-renderer-wrapper">
          <Page
            pageNumber={pageNumber}
            scale={zoom}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            className="pdf-page-shadow"
            // === VISUAL ADJUSTMENT ===
            // 1. brightness(0.94): Reduces pure white to a soft light gray (easier on eyes)
            // 2. contrast(1.02): Slightly boosts text sharpness to compensate for dimming
            // 3. sepia(0.05): Adds a very tiny warm tint (like paper) to remove blue light harshness
            style={{
              filter: 'brightness(0.94) contrast(1.02) sepia(0.05)', 
              display: 'block',
              userSelect: 'text'
            }}
            loading={
              <div className="page-loading">
                Loading page {pageNumber}...
              </div>
            }
          />
          {children}
        </div>
      </div>
    </Document>
  );
}